import type { LocationGeocodeResult } from '@/shared/lib/location';
import { LocateFixed, Search } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../button';
import { Input } from '../input';
import { Spinner } from '../spinner';
import { Text } from '../text';
import css from './location-picker-map.module.scss';

const fallbackCenter: [number, number] = [38.7223, -9.1393];

const coordinateKey = (latitude: number, longitude: number): string =>
  `${latitude.toFixed(6)},${longitude.toFixed(6)}`;

const MapCenterController = ({
  center,
  hasPosition,
  onCenterChange,
}: {
  center: [number, number];
  hasPosition: boolean;
  onCenterChange: (latitude: number, longitude: number) => void;
}) => {
  const map = useMap();
  const suppressNextMoveEnd = useRef(false);

  useEffect(() => {
    if (!hasPosition) return;
    const currentCenter = map.getCenter();
    if (
      coordinateKey(currentCenter.lat, currentCenter.lng) ===
      coordinateKey(center[0], center[1])
    ) {
      return;
    }
    suppressNextMoveEnd.current = true;
    map.setView(center, map.getZoom(), { animate: false });
  }, [center, hasPosition, map]);

  useMapEvents({
    moveend: (event) => {
      if (suppressNextMoveEnd.current) {
        suppressNextMoveEnd.current = false;
        return;
      }
      const nextCenter = event.target.getCenter();
      if (
        coordinateKey(nextCenter.lat, nextCenter.lng) ===
        coordinateKey(center[0], center[1])
      ) {
        return;
      }
      onCenterChange(nextCenter.lat, nextCenter.lng);
    },
  });

  return null;
};

type LocationPickerMapProps = {
  latitude: number | null;
  longitude: number | null;
  locationLabel: string;
  onPick: (latitude: number, longitude: number) => void;
  onSearchChange: (value: string) => void;
  onSelectResult: (result: LocationGeocodeResult) => void;
  search: string;
  searchError?: string;
  searchLoading: boolean;
  searchResults: LocationGeocodeResult[];
};

export const LocationPickerMap = ({
  latitude,
  longitude,
  locationLabel,
  onPick,
  onSearchChange,
  onSelectResult,
  search,
  searchError,
  searchLoading,
  searchResults,
}: LocationPickerMapProps) => {
  const hasPosition = latitude != null && longitude != null;
  const center: [number, number] = hasPosition
    ? [latitude, longitude]
    : fallbackCenter;

  return (
    <div className={css.mapPicker}>
      <div className={css.mapSearch}>
        <Input
          leadingIcon={<Search style={{ height: 14, width: 14 }} />}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search address"
          trailingIcon={searchLoading ? <Spinner size="sm" /> : undefined}
          value={search}
        />

        {searchError && (
          <Text as="p" size="xs" variant="muted">
            {searchError}
          </Text>
        )}

        {searchResults.length > 0 && (
          <div className={css.mapSearchResults} role="listbox">
            {searchResults.map((result, idx) => (
              <button
                className={css.mapSearchResult}
                key={result.id}
                onClick={() => onSelectResult(result)}
                role="option"
                tabIndex={idx === 0 ? 0 : -1}
                type="button"
              >
                {result.label}
              </button>
            ))}
          </div>
        )}

        {locationLabel && (
          <Text as="p" size="xs" variant="muted">
            Selected: {locationLabel}
          </Text>
        )}
      </div>

      <div className={css.mapCanvas}>
        <MapContainer
          center={center}
          className={css.leafletMap}
          scrollWheelZoom
          zoom={hasPosition ? 13 : 4}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapCenterController
            center={center}
            hasPosition={hasPosition}
            onCenterChange={onPick}
          />
        </MapContainer>
        <div aria-hidden="true" className={css.mapCenterMarker} />
      </div>

      <div className={css.mapActions}>
        <Button
          icon={<LocateFixed style={{ height: 14, width: 14 }} />}
          onClick={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition((position) => {
              onPick(position.coords.latitude, position.coords.longitude);
            });
          }}
          type="button"
          variant="secondary"
        >
          Use current location
        </Button>
        <Text size="xs" variant="muted">
          Drag the map to position the center marker.
        </Text>
      </div>
    </div>
  );
};
